import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Activity, MapPin, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Multi-Studio Auslastungs-Karte für Admin-Dashboard.
 *
 * Formel:
 *   Auslastung = gebuchte Trainer-Minuten / (verfügbare Trainer-Minuten - Blockierungen) × 100
 *
 * Toggle: Heute / Woche / Monat (Standard: Woche)
 */
const STATUS_COLOR = (pct) => {
  if (pct >= 80) return { bar: 'bg-rose-500', text: 'text-rose-700', bg: 'bg-rose-50' };
  if (pct >= 60) return { bar: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50' };
  if (pct >= 30) return { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' };
  return { bar: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-50' };
};

const formatMinutes = (m) => {
  if (!m || m < 60) return `${m || 0} Min`;
  const h = Math.floor(m / 60);
  const min = m % 60;
  return min ? `${h}h ${min}m` : `${h}h`;
};

const StudioUtilizationCard = () => {
  const navigate = useNavigate();
  const [window, setWindow] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/studios/utilization`, {
        params: { window },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window]);

  const studios = useMemo(() => data?.studios || [], [data]);
  const overall = data?.overall;
  const overallColor = overall ? STATUS_COLOR(overall.utilization_pct) : STATUS_COLOR(0);

  // Hide card if no studios with trainers OR multi-studio nicht aktiviert
  if (!loading && studios.filter(s => s.trainer_count > 0).length < 1) {
    return null;
  }

  return (
    <Card className="border-amber-100" data-testid="studio-utilization-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <Activity className="h-5 w-5 text-amber-500" />
            Studio-Auslastung
          </CardTitle>
          <div className="flex gap-1">
            {[
              { key: 'today', label: 'Heute' },
              { key: 'week', label: 'Woche' },
              { key: 'month', label: '30 T' },
            ].map((opt) => (
              <Button
                key={opt.key}
                variant={window === opt.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setWindow(opt.key)}
                className="h-7 px-2 text-xs"
                data-testid={`utilization-toggle-${opt.key}`}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> lädt...
          </div>
        ) : !data ? (
          <div className="text-sm text-muted-foreground italic py-4">
            Keine Daten verfügbar.
          </div>
        ) : (
          <>
            {/* Gesamt-Auslastung */}
            <div
              className={`p-3 rounded-lg ${overallColor.bg} border border-${overallColor.text.replace('text-', '')}/20`}
              data-testid="utilization-overall"
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-gray-700">GESAMT</span>
                <span className={`text-2xl font-bold ${overallColor.text}`}>
                  {overall.utilization_pct}%
                </span>
              </div>
              <div className="h-2 bg-white/60 rounded-full overflow-hidden">
                <div
                  className={`h-full ${overallColor.bar} transition-all duration-700`}
                  style={{ width: `${Math.min(100, overall.utilization_pct)}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1.5 text-xs text-gray-600">
                <span>{formatMinutes(overall.booked_minutes)} gebucht</span>
                <span>von {formatMinutes(overall.net_available_minutes)} verfügbar</span>
              </div>
            </div>

            {/* Pro Studio */}
            {studios.map((s) => {
              const c = STATUS_COLOR(s.utilization_pct);
              const hasTrainers = s.trainer_count > 0;
              return (
                <div
                  key={s.studio_id}
                  className="p-2.5 rounded-md hover:bg-slate-50 cursor-pointer transition"
                  onClick={() => navigate(`/appointments`)}
                  data-testid={`utilization-studio-${s.studio_name}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                      <MapPin className="h-3.5 w-3.5 text-slate-500" />
                      {s.studio_name}
                      {!hasTrainers && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5">
                          keine Trainer
                        </Badge>
                      )}
                    </div>
                    <span className={`text-base font-bold ${c.text}`}>
                      {s.utilization_pct}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${c.bar} transition-all duration-700`}
                      style={{ width: `${Math.min(100, s.utilization_pct)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1 text-[11px] text-gray-500">
                    <span>{s.appointment_count} Termine</span>
                    <span>
                      {s.trainer_count} Trainer · {formatMinutes(s.booked_minutes)} /{' '}
                      {formatMinutes(s.net_available_minutes)}
                      {s.blocked_minutes > 0 && (
                        <span className="ml-1 text-rose-500">
                          (−{formatMinutes(s.blocked_minutes)} blockiert)
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StudioUtilizationCard;
